import type { EditableCompanyProfile } from "@/lib/cat/company-profile.types";
import { EMPTY_EDITABLE_COMPANY_PROFILE } from "@/lib/cat/company-profile.types";
import type { InseeSiretPreview } from "@/lib/cat/insee-siret-mapper";
import { parseMdmAddressLines } from "@/lib/cat/mdm-address-line-uses";
import {
  extractPhonesFromMdmContacts,
  extractPrimaryEmailFromMdmContacts,
} from "@/lib/cat/mdm-map-contacts";
import { mdmScalarToTrimmedString } from "@/lib/cat/mdm-scalars";
import type { MdmAccount } from "@/types/cat/mdm-account.types";

const MDM_APE_IDENTIFIER_TYPES = new Set(["ape", "apen", "naf", "ape_code"]);

export function normalizeApeCode(value: string | null | undefined): string {
  return (value ?? "").trim().replace(/\./g, "").toUpperCase();
}

export function extractMdmCompanyApe(account: MdmAccount): string {
  for (const id of account.identifier) {
    const t = id.type?.toLowerCase() ?? "";
    if (!MDM_APE_IDENTIFIER_TYPES.has(t)) continue;

    const v = id.value;
    if (typeof v === "string") return v.trim();
    if (typeof v === "number") return String(v);
    if (v && typeof v === "object" && "Code" in v) {
      return mdmScalarToTrimmedString((v as { Code?: unknown }).Code);
    }
  }
  return "";
}

export function extractMdmSourceId(account: MdmAccount): string {
  const sourceId = account.identifier.find(
    (i) => i.type?.toLowerCase() === "sourceid",
  );
  if (sourceId?.value != null) {
    return mdmScalarToTrimmedString(sourceId.value);
  }
  return "";
}

export function extractMdmCompanySiret(account: MdmAccount): string {
  const siretIdentifier = account.identifier.find((id) => {
    const type = id.type?.trim().toLowerCase() ?? "";
    return type === "siretnb" || type === "siret";
  });
  if (typeof siretIdentifier?.value === "string") {
    return siretIdentifier.value.trim();
  }
  return "";
}

export function mapMdmCompanyToEditableProfile(
  account: MdmAccount,
  mdmBrandCode?: string | null,
): EditableCompanyProfile {
  const main =
    account.address?.find((a) => a.rank === "main") ?? account.address?.[0];
  const parsedAddress = parseMdmAddressLines(main?.line);
  const phones = extractPhonesFromMdmContacts(account.contact);

  return {
    companyDenomination:
      account.name.text?.trim() || account.name.lastname?.trim() || "",
    apeCode: extractMdmCompanyApe(account),
    email: extractPrimaryEmailFromMdmContacts(account.contact, mdmBrandCode),
    addressLine: parsedAddress.addressLine,
    addressFloorApartment: parsedAddress.addressFloorApartment,
    addressBuildingResidence: parsedAddress.addressBuildingResidence,
    postBox: parsedAddress.postBox,
    postalCode: mdmScalarToTrimmedString(main?.postalCode),
    city: mdmScalarToTrimmedString(main?.city),
    fixedPhone: phones.fixedPhone,
    mobilePhone: phones.mobilePhone,
  };
}

export function buildCompanyProfileFromInsee(
  preview: InseeSiretPreview,
): EditableCompanyProfile {
  return {
    ...EMPTY_EDITABLE_COMPANY_PROFILE,
    companyDenomination: preview.denomination?.trim() ?? "",
    apeCode: preview.ape?.trim() ?? "",
    addressLine: preview.addressLine?.trim() ?? "",
    addressFloorApartment: preview.addressFloorApartment?.trim() ?? "",
    addressBuildingResidence: preview.addressBuildingResidence?.trim() ?? "",
    postBox: preview.postBox?.trim() ?? "",
    postalCode: preview.postalCode?.trim() ?? "",
    city: preview.city?.trim() ?? "",
  };
}

const PREFILL_LOCKABLE_COMPANY_FIELDS = [
  "companyDenomination",
  "apeCode",
  "addressLine",
  "postalCode",
  "city",
  "addressFloorApartment",
  "addressBuildingResidence",
  "postBox",
] as const satisfies readonly (keyof EditableCompanyProfile)[];

export type CompanyPrefilledFieldLocks = Record<
  (typeof PREFILL_LOCKABLE_COMPANY_FIELDS)[number],
  boolean
>;

export function emptyPrefilledFieldLocks(): CompanyPrefilledFieldLocks {
  return {
    companyDenomination: false,
    apeCode: false,
    addressLine: false,
    postalCode: false,
    city: false,
    addressFloorApartment: false,
    addressBuildingResidence: false,
    postBox: false,
  };
}

/** Snapshot of fields that were non-empty when the profile was loaded from INSEE/MDM. */
export function buildPrefilledFieldLocks(
  profile: EditableCompanyProfile,
): CompanyPrefilledFieldLocks {
  return {
    companyDenomination: profile.companyDenomination.trim().length > 0,
    apeCode: profile.apeCode.trim().length > 0,
    addressLine: profile.addressLine.trim().length > 0,
    postalCode: profile.postalCode.trim().length > 0,
    city: profile.city.trim().length > 0,
    addressFloorApartment: profile.addressFloorApartment.trim().length > 0,
    addressBuildingResidence:
      profile.addressBuildingResidence.trim().length > 0,
    postBox: profile.postBox.trim().length > 0,
  };
}

export function mapMdmCompanyAccountFields(
  account: MdmAccount,
  mdmBrandCode?: string | null,
) {
  const profile = mapMdmCompanyToEditableProfile(account, mdmBrandCode);
  return {
    mdmSourceId: extractMdmSourceId(account),
    mdmSiret: extractMdmCompanySiret(account),
    mdmDenomination: profile.companyDenomination,
    mdmApe: profile.apeCode,
    mdmAddressLine: profile.addressLine,
    mdmAddressFloorApartment: profile.addressFloorApartment,
    mdmAddressBuildingResidence: profile.addressBuildingResidence,
    mdmPostBox: profile.postBox,
    mdmPostalCode: profile.postalCode,
    mdmCity: profile.city,
    mdmEmail: profile.email,
    mdmFixedPhone: profile.fixedPhone.value,
    mdmFixedPhoneUse: profile.fixedPhone.use,
    mdmMobilePhone: profile.mobilePhone.value,
    mdmMobilePhoneUse: profile.mobilePhone.use,
    profile,
  };
}

export function refPersCompanyToEditableProfile(
  company: import("@/lib/cat/retail-client.types").RetailCompany,
): EditableCompanyProfile {
  return {
    companyDenomination: company.mdmDenomination?.trim() ?? "",
    apeCode: company.mdmApe?.trim() ?? "",
    email: company.mdmEmail?.trim() ?? "",
    addressLine: company.mdmAddressLine?.trim() ?? "",
    addressFloorApartment: company.mdmAddressFloorApartment?.trim() ?? "",
    addressBuildingResidence: company.mdmAddressBuildingResidence?.trim() ?? "",
    postBox: company.mdmPostBox?.trim() ?? "",
    postalCode: company.mdmPostalCode?.trim() ?? "",
    city: company.mdmCity?.trim() ?? "",
    fixedPhone: {
      value: company.mdmFixedPhone?.trim() ?? "",
      use:
        company.mdmFixedPhoneUse?.trim().toLowerCase() === "work"
          ? "work"
          : company.mdmFixedPhoneUse?.trim().toLowerCase() === "home"
            ? "home"
            : "unknown",
    },
    mobilePhone: {
      value: company.mdmMobilePhone?.trim() ?? "",
      use:
        company.mdmMobilePhoneUse?.trim().toLowerCase() === "work"
          ? "work"
          : company.mdmMobilePhoneUse?.trim().toLowerCase() === "home"
            ? "home"
            : "unknown",
    },
  };
}
